import { useCallback, useEffect, useMemo, useState } from "react";
import { LogOut, Search, ShoppingBag, X } from "lucide-react";
import { garments } from "../data/garments";
import { fetchOrders, fetchSentOrders, signOut, submitOrder } from "../lib/firebase";
import { combineOrderHistory, getOrderTotal } from "../lib/orders";
import { validateOrder } from "../lib/orderValidation";
import type { CartItem, CurrentOrders, CustomerDetails, Feedback, OrderValidationErrors } from "../types";
import { CartPanel } from "./CartPanel";
import { BrandMark } from "./BrandMark";
import { FeedbackToast } from "./FeedbackToast";
import { OrderConfirmation } from "./OrderConfirmation";
import { ProductGrid } from "./ProductGrid";

const emptyCustomer: CustomerDetails = { name: "", email: "", phone: "" };

function errorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return "Något gick fel. Försök igen.";
}

export function Storefront() {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [orderHistory, setOrderHistory] = useState<CurrentOrders>({});
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer, setCustomer] = useState<CustomerDetails>(emptyCustomer);
  const [query, setQuery] = useState("");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [validationErrors, setValidationErrors] = useState<OrderValidationErrors>({});

  const loadOrderData = useCallback(async () => {
    const [currentOrders, sentOrders] = await Promise.all([fetchOrders(), fetchSentOrders()]);
    setCounts(Object.fromEntries(
      Object.entries(currentOrders).map(([garment, orders]) => [garment, getOrderTotal(orders)]),
    ));
    setOrderHistory(combineOrderHistory(currentOrders, sentOrders));
  }, []);

  useEffect(() => {
    void loadOrderData().catch(() => {
      setFeedback({ tone: "error", message: "Tidigare storleksval kunde inte hämtas." });
    });
  }, [loadOrderData]);

  useEffect(() => {
    document.body.classList.toggle("drawer-open", isCartOpen);
    return () => document.body.classList.remove("drawer-open");
  }, [isCartOpen]);

  const filteredGarments = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("sv");
    return garments
      .filter((garment) =>
        `${garment.name} ${garment.supplierName}`.toLocaleLowerCase("sv").includes(normalized),
      )
      .map((garment) => ({ ...garment, ordered: counts[garment.name] ?? 0 }));
  }, [counts, query]);

  function handleAdd(garmentName: string, size: string) {
    setCart((items) => [
      ...items,
      { id: crypto.randomUUID(), garmentName, size },
    ]);
    setValidationErrors((errors) => {
      const nextErrors = { ...errors };
      delete nextErrors.cart;
      return nextErrors;
    });
  }

  async function handleSubmit() {
    const errors = validateOrder(customer, cart);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFeedback(null);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitOrder({
        name: customer.name.trim(),
        email: customer.email.trim(),
        phone: customer.phone.trim(),
      }, cart);
      setCart([]);
      setCustomer(emptyCustomer);
      setValidationErrors({});
      setIsCartOpen(false);
      setIsConfirmationOpen(true);
      void loadOrderData().catch(() => {
        // The order is complete even if the displayed totals cannot be refreshed.
      });
    } catch (error) {
      setFeedback({ tone: "error", message: errorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="store-layout">
      <CartPanel
        cart={cart}
        customer={customer}
        isOpen={isCartOpen}
        isSubmitting={isSubmitting}
        validationErrors={validationErrors}
        onClose={() => setIsCartOpen(false)}
        onCustomerChange={(nextCustomer) => {
          setCustomer(nextCustomer);
          setValidationErrors((errors) => {
            const nextErrors = { ...errors };
            const updatedErrors = validateOrder(nextCustomer, cart);
            if (errors.name && customer.name !== nextCustomer.name) {
              if (updatedErrors.name) nextErrors.name = updatedErrors.name;
              else delete nextErrors.name;
            }
            const contactChanged = customer.email !== nextCustomer.email || customer.phone !== nextCustomer.phone;
            const hasContactError = errors.contact || errors.email || errors.phone;
            if (hasContactError && contactChanged) {
              delete nextErrors.contact;
              delete nextErrors.email;
              delete nextErrors.phone;
              if (updatedErrors.contact) nextErrors.contact = updatedErrors.contact;
              if (updatedErrors.email) nextErrors.email = updatedErrors.email;
              if (updatedErrors.phone) nextErrors.phone = updatedErrors.phone;
            }
            return nextErrors;
          });
        }}
        onRemove={(id) => setCart((items) => items.filter((item) => item.id !== id))}
        onSubmit={handleSubmit}
      />
      {isCartOpen && (
        <button className="drawer-backdrop" type="button" onClick={() => setIsCartOpen(false)} aria-label="Stäng beställningen" />
      )}

      <main className="store-main">
        <header className="store-header">
          <BrandMark />
          <div className="store-header__actions">
            <button className="quiet-button" type="button" onClick={() => void signOut()} aria-label="Logga ut">
              <LogOut size={18} aria-hidden="true" />
              <span>Logga ut</span>
            </button>
            <button
              className="cart-trigger"
              type="button"
              onClick={() => setIsCartOpen(true)}
              aria-label={`Öppna beställningen, ${cart.length} plagg`}
            >
              <ShoppingBag size={19} aria-hidden="true" />
              <span>Beställning</span>
              <strong>{cart.length}</strong>
            </button>
          </div>
        </header>

        <section className="catalog-hero">
          <div>
            <span className="eyebrow">Beställ klubbkläder</span>
            <h1>Kils OK klubbkläder.</h1>
            <p>Välj dina plagg och storlekar. Vi samlar klubbens beställningar och skickar beställningar ett par gånger per år.</p>
          </div>
          <div className="catalog-stat">
            <strong>{garments.length}</strong>
            <span>plagg i kollektionen</span>
          </div>
        </section>

        <section className="catalog" aria-labelledby="catalog-title">
          <div className="catalog-toolbar">
            <div>
              <span className="eyebrow">Sortiment</span>
              <h2 id="catalog-title">Hitta ditt nästa klubbplagg</h2>
            </div>
            <label className="search-field">
              <Search size={19} aria-hidden="true" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Sök bland plagg" />
              {query && (
                <button type="button" onClick={() => setQuery("")} aria-label="Rensa sökning">
                  <X size={17} />
                </button>
              )}
            </label>
          </div>
          <ProductGrid garments={filteredGarments} orderHistory={orderHistory} onAdd={handleAdd} />
        </section>
      </main>

      {feedback && <FeedbackToast feedback={feedback} onClose={() => setFeedback(null)} />}
      {isConfirmationOpen && <OrderConfirmation onClose={() => setIsConfirmationOpen(false)} />}
    </div>
  );
}
